/**
 * Gerador de números pseudoaleatórios determinístico (xoshiro128**).
 *
 * O motor inteiro depende deste arquivo para qualquer decisão aleatória.
 * Nada mais no motor pode chamar `Math.random`. Isso é o que garante que a
 * mesma semente produza exatamente o mesmo resultado no servidor e no cliente,
 * permitindo replay, auditoria e testes estáveis.
 */
function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
function splitmix32(state) {
    let a = state | 0;
    return () => {
        a = (a + 0x9e3779b9) | 0;
        let t = a ^ (a >>> 16);
        t = Math.imul(t, 0x21f0aaad);
        t = t ^ (t >>> 15);
        t = Math.imul(t, 0x735a2d97);
        return (t ^ (t >>> 15)) >>> 0;
    };
}
function rotl(x, k) {
    return ((x << k) | (x >>> (32 - k))) >>> 0;
}
export class Rng {
    seed;
    s0;
    s1;
    s2;
    s3;
    spare = null;
    constructor(seed) {
        this.seed = String(seed);
        const next = splitmix32(fnv1a(this.seed));
        this.s0 = next();
        this.s1 = next();
        this.s2 = next();
        this.s3 = next();
        // Descarta os primeiros valores para afastar sementes parecidas.
        for (let i = 0; i < 12; i++)
            this.u32();
    }
    /** Cria um gerador filho determinístico. Não consome o gerador atual. */
    derive(...parts) {
        return new Rng(`${this.seed}|${parts.join(":")}`);
    }
    static of(...parts) {
        return new Rng(parts.join(":"));
    }
    /** Inteiro sem sinal de 32 bits. */
    u32() {
        const result = (Math.imul(rotl(Math.imul(this.s1, 5) >>> 0, 7), 9) >>> 0);
        const t = (this.s1 << 9) >>> 0;
        this.s2 = (this.s2 ^ this.s0) >>> 0;
        this.s3 = (this.s3 ^ this.s1) >>> 0;
        this.s1 = (this.s1 ^ this.s2) >>> 0;
        this.s0 = (this.s0 ^ this.s3) >>> 0;
        this.s2 = (this.s2 ^ t) >>> 0;
        this.s3 = rotl(this.s3, 11);
        return result;
    }
    /** Número real em [0, 1). */
    float() {
        return this.u32() / 4294967296;
    }
    /** Inteiro em [min, max], ambos inclusivos. */
    int(min, max) {
        if (max < min)
            throw new Error(`Rng.int: intervalo inválido ${min}..${max}`);
        return min + Math.floor(this.float() * (max - min + 1));
    }
    /** Verdadeiro com probabilidade `p` (0 a 1). */
    chance(p) {
        return this.float() < p;
    }
    pick(items) {
        if (items.length === 0)
            throw new Error("Rng.pick: lista vazia");
        return items[this.int(0, items.length - 1)];
    }
    /** Escolhe um item com probabilidade proporcional ao peso. Pesos negativos contam como zero. */
    pickWeighted(items, weight) {
        if (items.length === 0)
            throw new Error("Rng.pickWeighted: lista vazia");
        let total = 0;
        const weights = [];
        for (const item of items) {
            const w = Math.max(0, weight(item));
            weights.push(w);
            total += w;
        }
        if (total <= 0)
            return this.pick(items);
        let roll = this.float() * total;
        for (let i = 0; i < items.length; i++) {
            roll -= weights[i];
            if (roll <= 0)
                return items[i];
        }
        return items[items.length - 1];
    }
    /** Embaralha no lugar (Fisher-Yates) e devolve o mesmo array. */
    shuffle(items) {
        for (let i = items.length - 1; i > 0; i--) {
            const j = this.int(0, i);
            const tmp = items[i];
            items[i] = items[j];
            items[j] = tmp;
        }
        return items;
    }
    /** Amostra de uma normal usando Box-Muller, com valor reserva em cache. */
    gauss(mean = 0, sd = 1) {
        if (this.spare !== null) {
            const value = this.spare;
            this.spare = null;
            return mean + sd * value;
        }
        let u = 0;
        let v = 0;
        let s = 0;
        do {
            u = this.float() * 2 - 1;
            v = this.float() * 2 - 1;
            s = u * u + v * v;
        } while (s === 0 || s >= 1);
        const factor = Math.sqrt((-2 * Math.log(s)) / s);
        this.spare = v * factor;
        return mean + sd * u * factor;
    }
    /** Normal truncada e arredondada, útil para atributos de 1 a 100. */
    gaussInt(mean, sd, min, max) {
        const value = Math.round(this.gauss(mean, sd));
        return value < min ? min : value > max ? max : value;
    }
}
export function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
//# sourceMappingURL=rng.js.map